import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import {
  useHideAllNotificationsMutation,
  useHideNotificationMutation,
  useMarkAllNotificationsReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsInfiniteQuery,
  useNotificationsStore,
  useUnreadCountQuery,
  type NotificationDto,
  type NotificationReadFilter,
  type NotificationTone,
} from '@features/Notifications';
import { cn } from '@shared/lib';

const TONE_DOT: Record<NotificationTone, string> = {
  info: 'bg-fg/40',
  success: 'bg-success',
  warning: 'bg-warn',
  error: 'bg-danger',
};

const TONE_BORDER: Record<NotificationTone, string> = {
  info: 'border-line',
  success: 'border-success/35',
  warning: 'border-warn/35',
  error: 'border-danger/35',
};

const FILTERS: { key: NotificationReadFilter; labelKey: string }[] = [
  { key: 'all', labelKey: 'notifications.filters.all' },
  { key: 'unread', labelKey: 'notifications.filters.unread' },
  { key: 'read', labelKey: 'notifications.filters.read' },
];

export default function NotificationsPage() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<NotificationReadFilter>('all');
  const query = useNotificationsInfiniteQuery(filter);
  useUnreadCountQuery();
  const unreadCount = useNotificationsStore((s) => s.unreadCount);

  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();
  const hide = useHideNotificationMutation();
  const hideAll = useHideAllNotificationsMutation();
  // Скрытие не отменить: первое нажатие спрашивает, второе скрывает (без браузерного confirm).
  const [hideAllArmed, setHideAllArmed] = useState(false);
  const armTimer = useRef<number | null>(null);
  useEffect(() => () => {
    if (armTimer.current) window.clearTimeout(armTimer.current);
  }, []);
  useEffect(() => setHideAllArmed(false), [filter]);
  const clickHideAll = () => {
    if (armTimer.current) window.clearTimeout(armTimer.current);
    if (hideAllArmed) {
      setHideAllArmed(false);
      hideAll.mutate(filter);
      return;
    }
    setHideAllArmed(true);
    armTimer.current = window.setTimeout(() => setHideAllArmed(false), 3000);
  };

  const items = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!query.hasNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !query.isFetchingNextPage) {
          void query.fetchNextPage();
        }
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [query]);

  const isInitialLoading = query.isPending && items.length === 0;
  const isEmpty = !isInitialLoading && !query.isError && items.length === 0;

  return (
    <div className="mx-auto max-w-[820px] animate-fadeIn px-4 pb-20 pt-16 sm:px-6 nav:px-12 nav:pt-10">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-balance pb-1.5 font-serif text-[30px] font-medium leading-[1.2] tracking-[-0.02em] sm:text-[36px] nav:text-[44px]">
            {t('notifications.title')}
          </h1>
          <div className="mt-2.5 text-[14px] tracking-[0.02em] text-muted">
            {unreadCount > 0
              ? t('notifications.unreadCount', { count: unreadCount })
              : t('notifications.allRead')}
          </div>
        </div>
        {(unreadCount > 0 || items.length > 0) && (
          <div className="mb-1 flex flex-wrap gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="inline-flex shrink-0 items-center gap-2 rounded-pill border border-line bg-card px-3.5 py-1.5 text-[14px] font-medium text-fg/85 transition-colors hover:bg-elev disabled:opacity-60"
              >
                <CheckAllIcon />
                {t('notifications.markAllRead')}
              </button>
            )}
            {items.length > 0 && (
              <button
                type="button"
                onClick={clickHideAll}
                disabled={hideAll.isPending}
                className={cn(
                  'inline-flex shrink-0 items-center gap-2 rounded-pill border px-3.5 py-1.5 text-[14px] font-medium transition-colors disabled:opacity-60',
                  hideAllArmed
                    ? 'border-danger/50 bg-danger/10 text-danger'
                    : 'border-line bg-card text-fg/85 hover:bg-elev',
                )}
              >
                <HideAllIcon />
                {hideAllArmed ? t('notifications.hideAllConfirm') : t('notifications.hideAll')}
              </button>
            )}
          </div>
        )}
      </header>

      <div className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                'rounded-pill border px-3.5 py-1.5 text-[14px] font-medium transition-colors',
                active
                  ? 'border-accent bg-accent text-fg-inv'
                  : 'border-line bg-card text-fg/85 hover:bg-elev',
              )}
            >
              {t(f.labelKey)}
              {f.key === 'unread' && unreadCount > 0 && (
                <span
                  className={cn(
                    'ml-2 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-pill px-1.5 text-[12px] font-medium leading-none tabular-nums',
                    active
                      ? 'bg-fg-inv/20 text-fg-inv'
                      : 'bg-accent/15 text-accent',
                  )}
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {query.isError && !items.length && (
        <div className="rounded-card border border-danger/35 bg-[#1f1010] px-5 py-4 text-[14px] text-fg/80">
          {t('notifications.error')}
        </div>
      )}

      {isInitialLoading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[88px] animate-skeleton rounded-card bg-card" />
          ))}
        </div>
      )}

      {isEmpty && <EmptyState filter={filter} t={t} />}

      <ul className="flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {items.map((n) => (
            <motion.li
              key={n.id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.22, ease: [0.2, 0.9, 0.2, 1] }}
            >
              <Row
                n={n}
                onMarkRead={() => markRead.mutate(n.id)}
                onHide={() => hide.mutate(n.id)}
                t={t}
              />
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      {items.length > 0 && (
        <div ref={sentinelRef} className="flex justify-center py-6 text-[12px] text-muted-2">
          {query.isFetchingNextPage
            ? t('notifications.loadingMore')
            : query.hasNextPage
            ? ''
            : ''}
        </div>
      )}
    </div>
  );
}

interface RowProps {
  n: NotificationDto;
  onMarkRead: () => void;
  onHide: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function Row({ n, onMarkRead, onHide, t }: RowProps) {
  const isUnread = !n.isRead;
  return (
    <div
      className={cn(
        'flex items-start gap-3.5 rounded-card border bg-card px-5 py-4 transition-colors',
        TONE_BORDER[n.tone],
        isUnread && 'bg-elev',
      )}
    >
      <div className="mt-1.5 shrink-0">
        <span
          aria-hidden
          className={cn(
            'block h-2 w-2 rounded-full',
            isUnread ? TONE_DOT[n.tone] : 'bg-transparent ring-1 ring-line',
          )}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <div
            className={cn(
              'truncate text-[14px] font-semibold leading-snug',
              isUnread ? 'text-fg' : 'text-fg/85',
            )}
          >
            {n.title}
          </div>
          <div className="shrink-0 text-[12px] uppercase tracking-[0.12em] text-muted-2">
            {formatRelative(n.createdAt, t)}
          </div>
        </div>
        <div className="mt-1 text-[14px] leading-relaxed text-fg/70">{n.body}</div>
        <div className="mt-2.5 flex flex-wrap items-center gap-3 text-[12px]">
          {n.link && (
            <a
              href={n.link}
              className={cn(
                'rounded-pill border px-3 py-1.5 font-medium transition-colors',
                n.tone === 'error'
                  ? 'border-danger/40 bg-danger/10 text-danger hover:bg-danger/20'
                  : 'border-line bg-base text-fg/85 hover:text-accent',
              )}
            >
              {n.actionLabel ?? t('notifications.item.open')}
            </a>
          )}
          {isUnread && (
            <button
              type="button"
              onClick={onMarkRead}
              className="text-muted underline-offset-2 hover:text-fg hover:underline"
            >
              {t('notifications.item.markRead')}
            </button>
          )}
          <button
            type="button"
            onClick={onHide}
            className="text-muted underline-offset-2 hover:text-fg hover:underline"
          >
            {t('notifications.item.hide')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EmptyStateProps {
  filter: NotificationReadFilter;
  t: ReturnType<typeof useTranslation>['t'];
}

function EmptyState({ filter, t }: EmptyStateProps) {
  if (filter === 'unread') {
    return (
      <div className="rounded-card border border-line bg-card px-6 py-10 text-center text-[14px] text-muted">
        {t('notifications.emptyUnread')}
      </div>
    );
  }
  if (filter === 'read') {
    return (
      <div className="rounded-card border border-line bg-card px-6 py-10 text-center text-[14px] text-muted">
        {t('notifications.emptyRead')}
      </div>
    );
  }
  return (
    <div className="rounded-card border border-line bg-card px-5 py-10 text-center nav:px-8 nav:py-12">
      <div className="font-serif text-[24px] font-medium tracking-[-0.01em] text-fg">
        {t('notifications.empty.title')}
      </div>
      <div className="mx-auto mt-2 max-w-[420px] text-[14px] leading-relaxed text-muted">
        {t('notifications.empty.sub')}
      </div>
    </div>
  );
}

function formatRelative(iso: string, t: ReturnType<typeof useTranslation>['t']): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return '';
  const diffMs = Date.now() - ts;
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return t('notifications.relativeNow');
  if (min < 60) return t('notifications.relativeMin', { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t('notifications.relativeHour', { count: h });
  const d = Math.floor(h / 24);
  return t('notifications.relativeDay', { count: d });
}

function CheckAllIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 11.5l.5.5 6-7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function HideAllIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 8s2.2-4.5 6-4.5S14 8 14 8s-2.2 4.5-6 4.5S2 8 2 8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M3 13L13 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}
